import { ICitation, ICitationHighlightArea } from "@/api";
import { ViewerToolbar } from "@/components/shared/ViewerToolbar";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { styles, theme } from "@/config/project";
import { useOutputDocumentDownload } from "@/hooks/useOutputDocumentDownload";
import {
    DocumentViewerStatus,
    OutputDocumentDownloadCapability,
    isSpreadsheet,
} from "@/pages/frontend/chat/contexts/DocumentViewerContext.types";
import { useDocumentViewer } from "@/pages/frontend/chat/contexts/useDocumentViewer";
import { Box, IconButton, keyframes, Stack, Typography } from "@mui/material";
import { Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import type { RenderHighlightsProps } from "@react-pdf-viewer/highlight";
import { highlightPlugin, Trigger } from "@react-pdf-viewer/highlight";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { AlertCircle, Download, X } from "lucide-react";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { ClickablePageReferences } from "./ClickablePageReferences";
import { reportLinkPlugin } from "./reportLinkPlugin";
import { SpreadsheetViewerPanel } from "./SpreadsheetViewerPanel";
import { DocumentViewerTabStrip } from "./DocumentViewerTabStrip";

const shimmer = keyframes`
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
`;

const HIGHLIGHT_OPACITY = 0.4;
const EMPTY_HIGHLIGHTS: ICitationHighlightArea[] = [];

// Shared shell for every panel state. `fade-in-70` reproduces the old
// panelFadeIn keyframe (opacity 0.7 -> 1) so the entrance stays identical.
const PANEL_CONTAINER_CLASS =
    "animate-in fade-in-70 flex h-full min-w-0 flex-col overflow-hidden border-l bg-muted/30  duration-200 ease-out";

interface IRenderHighlightsProps extends RenderHighlightsProps {
    citationAreas: ICitationHighlightArea[];
}

const renderHighlights = ({
    citationAreas,
    pageIndex,
    getCssProperties,
    rotation,
}: IRenderHighlightsProps) => (
    <div>
        {citationAreas
            .filter((area) => area.page_index === pageIndex)
            .map((area, idx) => (
                <div
                    key={idx}
                    className="highlight-area"
                    style={Object.assign(
                        {},
                        {
                            background: theme.palette.ochre.light,
                            opacity: HIGHLIGHT_OPACITY,
                        },
                        getCssProperties(
                            { pageIndex: area.page_index, ...area },
                            rotation
                        )
                    )}
                />
            ))}
    </div>
);

interface IPanelHeaderProps {
    documentTitle: string;
    status: DocumentViewerStatus;
    selectedSource: ICitation | null;
    outputDownload: OutputDocumentDownloadCapability | null;
    closeDocument: () => void;
    pageNavigationPluginInstance: ReturnType<typeof pageNavigationPlugin>;
}

// Module-scope component; stable type identity avoids remount on every parent render
const PanelHeader = ({
    documentTitle,
    status,
    selectedSource,
    outputDownload,
    closeDocument,
    pageNavigationPluginInstance,
}: IPanelHeaderProps) => {
    const download = useOutputDocumentDownload();
    const showDownload = status === "ready" && outputDownload !== null;

    return (
        <div className="bg-muted/30 flex shrink-0 items-center justify-between border-b px-4 py-3">
            <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    title={documentTitle}
                >
                    {documentTitle}
                </Typography>
                {status === "ready" && selectedSource?.pages && (
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{
                            color: "text.secondary",
                            fontFamily: "monospace",
                            mt: 0.25,
                        }}
                    >
                        <ClickablePageReferences
                            pageString={selectedSource.pages}
                            pageNavigationPluginInstance={
                                pageNavigationPluginInstance
                            }
                        />
                    </Typography>
                )}
            </Box>
            <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexShrink: 0 }}
            >
                {showDownload && (
                    <Tooltip>
                        <TooltipTrigger
                            render={
                                <Button
                                    type="button"
                                    size="icon-sm"
                                    variant="ghost"
                                    className="text-primary hover:text-primary"
                                    aria-label="Download"
                                    disabled={download.isPending}
                                    aria-busy={download.isPending}
                                    onClick={() =>
                                        download.mutate(
                                            outputDownload.documentId
                                        )
                                    }
                                />
                            }
                        >
                            <Download aria-hidden />
                        </TooltipTrigger>
                        <TooltipContent side="bottom">Download</TooltipContent>
                    </Tooltip>
                )}
                <IconButton
                    size="small"
                    aria-label="Close document viewer"
                    onClick={closeDocument}
                >
                    <X size={16} />
                </IconButton>
            </Stack>
        </div>
    );
};

const ShimmerBar = ({
    width,
    height = 12,
    delay = "0s",
}: {
    width: number | string;
    height?: number;
    delay?: string;
}) => (
    <Box
        sx={{
            width,
            height,
            borderRadius: 1,
            background: `linear-gradient(90deg, #e8ecf1 25%, #f0f3f7 50%, #e8ecf1 75%)`,
            backgroundSize: "800px 100%",
            animation: `${shimmer} 1.5s ease-in-out infinite`,
            animationDelay: delay,
        }}
    />
);

export const DocumentViewerPanel = () => {
    const {
        status,
        selectedSource,
        initialPage,
        outputDownload,
        closeDocument,
        jumpToPageRef,
        tabs,
        activeTabId,
        activateTab,
        closeTab,
        setTabPage,
        holdTabPdf,
        isTabActivation,
        openDocumentById,
    } = useDocumentViewer();
    const panelRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const capturedRequestIdRef = useRef(0);
    const escapeFocusRef = useRef<string | null>(null);
    const linkFocusRef = useRef(false);
    const [showTabStrip, setShowTabStrip] = useState(false);
    const activeTab = tabs.find((tab) => tab.id === activeTabId);
    const openerRequestId =
        activeTab?.openMode === "open"
            ? activeTab.openedWith.requestId
            : undefined;

    useLayoutEffect(() => {
        if (status === "idle") {
            capturedRequestIdRef.current = 0;
            openerRef.current = null;
            return;
        }
        if (
            openerRequestId === undefined ||
            openerRequestId <= capturedRequestIdRef.current
        )
            return;
        capturedRequestIdRef.current = openerRequestId;
        const focused = document.activeElement;
        openerRef.current =
            focused instanceof HTMLElement &&
            focused !== document.body &&
            !panelRef.current?.contains(focused)
                ? focused
                : null;
    }, [openerRequestId, status]);

    const restoreFocus = useCallback(() => {
        const panel = panelRef.current;
        const focused = document.activeElement;
        if (
            focused instanceof HTMLElement &&
            focused !== document.body &&
            !panel?.contains(focused)
        )
            return;
        const scope =
            panel?.closest('[role="dialog"]') ??
            panel?.closest('[data-slot="resizable-panel-group"]');
        const controls = scope
            ? Array.from(
                  scope.querySelectorAll<HTMLElement>(
                      "button, a[href], input, textarea, select, [tabindex]"
                  )
              )
            : [];
        const candidates = [
            openerRef.current,
            ...controls.filter((control) =>
                control.matches('textarea, [contenteditable="true"]')
            ),
            ...controls,
        ];
        for (const candidate of candidates) {
            if (
                !candidate?.isConnected ||
                panel?.contains(candidate) ||
                candidate.tabIndex < 0 ||
                candidate.matches(':disabled, [aria-disabled="true"]') ||
                candidate.closest('[inert], [hidden], [aria-hidden="true"]')
            )
                continue;
            candidate.focus();
            if (document.activeElement === candidate) return;
        }
    }, []);

    const closeViewer = useCallback(() => {
        restoreFocus();
        closeDocument();
    }, [closeDocument, restoreFocus]);

    const closeViewerTab = useCallback(
        (id: string) => {
            const next = closeTab(id);
            if (next === null) restoreFocus();
            return next;
        },
        [closeTab, restoreFocus]
    );

    useLayoutEffect(() => {
        const next = escapeFocusRef.current;
        if (next === null) return;
        escapeFocusRef.current = null;
        if (activeTabId !== next) return;
        const panel = panelRef.current;
        const focused = document.activeElement;
        if (focused !== document.body && !panel?.contains(focused)) return;
        panel
            ?.querySelector<HTMLButtonElement>(
                '[role="tab"][aria-selected="true"]'
            )
            ?.focus();
    }, [activeTabId, tabs]);

    useLayoutEffect(() => {
        if (!linkFocusRef.current) return;
        if (
            activeTab?.openMode !== "open" ||
            activeTab.openedWith.origin !== "report-link"
        ) {
            linkFocusRef.current = false;
            return;
        }
        const panel = panelRef.current;
        const selected = panel?.querySelector<HTMLButtonElement>(
            '[role="tab"][aria-selected="true"]'
        );
        if (!selected) return;
        linkFocusRef.current = false;
        const focused = document.activeElement;
        if (focused !== document.body && !panel?.contains(focused)) return;
        selected.focus();
    }, [activeTab, showTabStrip]);

    useLayoutEffect(() => {
        if (tabs.length > 1) setShowTabStrip(true);
        else if (tabs.length === 0) setShowTabStrip(false);
    }, [tabs.length]);

    // Ref so renderHighlights reads the latest value without recreating the plugin.
    const highlightAreasRef =
        useRef<ICitationHighlightArea[]>(EMPTY_HIGHLIGHTS);
    highlightAreasRef.current =
        selectedSource?.highlight_areas ?? EMPTY_HIGHLIGHTS;

    // Plugin factories call React hooks internally, so they MUST be called at
    // component top level, not inside useMemo callbacks.
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: ViewerToolbar,
        sidebarTabs: () => [],
    });

    const highlightPluginInstance = highlightPlugin({
        renderHighlights: (props) =>
            renderHighlights({
                ...props,
                citationAreas: highlightAreasRef.current,
            }),
        trigger: Trigger.None,
    });

    const pageNavigationPluginInstance = pageNavigationPlugin();

    // One instance for the panel's life: the Viewer uninstalls the instances it
    // installed, so a per-render instance would leave later pages' links bound.
    const openDocumentByIdRef = useRef(openDocumentById);
    openDocumentByIdRef.current = openDocumentById;
    const showsReportTwinRef = useRef(false);
    showsReportTwinRef.current = activeTab?.variant === "report_internal";
    const [reportLinkPluginInstance] = useState(() =>
        reportLinkPlugin(
            ({ documentId, page }) => {
                linkFocusRef.current = true;
                void openDocumentByIdRef.current(documentId, page, {
                    origin: "report-link",
                    target: "new-tab",
                });
            },
            () => showsReportTwinRef.current
        )
    );

    // Register jumpToPage in the shared ref so the Provider can trigger page jumps
    // without remounting the Viewer (same-document navigation)
    useEffect(() => {
        jumpToPageRef.current = pageNavigationPluginInstance.jumpToPage;
        return () => {
            jumpToPageRef.current = null;
        };
    }, [jumpToPageRef, pageNavigationPluginInstance.jumpToPage]);

    const handleEscape = useCallback(
        (e: KeyboardEvent) => {
            if (e.key !== "Escape" || e.defaultPrevented || status === "idle")
                return false;
            const tag = (e.target as Element)?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return false;
            if (document.querySelector(".MuiModal-root")) return false;
            e.preventDefault();
            if (showTabStrip && activeTabId !== null) {
                escapeFocusRef.current = closeViewerTab(activeTabId);
            } else {
                closeViewer();
            }
            return true;
        },
        [activeTabId, closeViewer, closeViewerTab, showTabStrip, status]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [handleEscape]);

    if (status === "idle") return null;

    const documentTitle =
        status === "loading"
            ? "Loading document..."
            : status === "error"
              ? "Document unavailable"
              : status === "unsupported"
                ? (selectedSource?.document_name ?? "Untitled spreadsheet")
                : (selectedSource?.document_name ?? "");

    const headerProps: IPanelHeaderProps = {
        documentTitle,
        status,
        selectedSource,
        outputDownload,
        closeDocument: closeViewer,
        pageNavigationPluginInstance,
    };

    const renderPanel = (content: ReactNode) => (
        <div
            ref={panelRef}
            className={PANEL_CONTAINER_CLASS}
            onKeyDown={(event) => {
                if (handleEscape(event.nativeEvent)) event.stopPropagation();
            }}
        >
            <PanelHeader {...headerProps} />
            {showTabStrip && (
                <DocumentViewerTabStrip
                    tabs={tabs.map((tab) => ({
                        id: tab.id,
                        title:
                            (tab.selectedSource?.document_id === tab.documentId
                                ? tab.selectedSource.document_name
                                : "") ||
                            (tab.status === "error"
                                ? "Document unavailable"
                                : tab.id !== activeTabId
                                  ? "Document not loaded"
                                  : "Loading document..."),
                        kind: isSpreadsheet(tab.fileType)
                            ? "spreadsheet"
                            : "document",
                    }))}
                    activeTabId={activeTabId}
                    onActivate={(id) => void activateTab(id)}
                    onClose={closeViewerTab}
                />
            )}
            {content}
        </div>
    );

    if (status === "loading") {
        return renderPanel(
            <Box
                sx={{
                    flex: 1,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <Stack spacing={1.5}>
                    <ShimmerBar width={280} />
                    <ShimmerBar width={220} delay="0.15s" />
                    <ShimmerBar width={180} delay="0.3s" />
                </Stack>
                <Box
                    sx={{
                        flex: 1,
                        borderRadius: 1,
                        background: `linear-gradient(90deg, #e8ecf1 25%, #f0f3f7 50%, #e8ecf1 75%)`,
                        backgroundSize: "800px 100%",
                        animation: `${shimmer} 1.5s ease-in-out infinite`,
                        animationDelay: "0.1s",
                        minHeight: 200,
                    }}
                />
            </Box>
        );
    }

    if (status === "error") {
        return renderPanel(
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    p: 3,
                }}
            >
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        bgcolor: styles.errorLight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <AlertCircle
                        size={28}
                        color={styles.errorColor}
                        strokeWidth={1.5}
                    />
                </Box>
                <Stack spacing={0.5} alignItems="center">
                    <Typography variant="body1" fontWeight={600}>
                        Unable to load document
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", maxWidth: 240 }}
                    >
                        The document couldn't be retrieved. Please try again
                        later.
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (!selectedSource) return null;

    // Spreadsheets have no in-app preview: render the guarded panel in the
    // shared chrome instead of mounting the @react-pdf-viewer.
    if (status === "unsupported") {
        return renderPanel(
            <Box sx={{ flex: 1, overflow: "hidden" }}>
                <SpreadsheetViewerPanel
                    key={`${activeTabId}:${selectedSource.document_id}`}
                    status="ready"
                    documentName={selectedSource.document_name}
                    documentId={selectedSource.document_id}
                    downloadAvailable={Boolean(selectedSource.document_url)}
                    focusGuard={panelRef}
                    autoFocus={!isTabActivation}
                />
            </Box>
        );
    }

    return renderPanel(
        <Box sx={{ flex: 1, overflow: "hidden" }}>
            <Viewer
                key={`${activeTabId}:${selectedSource.document_id}`}
                fileUrl={selectedSource.document_url ?? ""}
                initialPage={initialPage ? initialPage - 1 : 0}
                onPageChange={({ currentPage }) => {
                    if (activeTabId) setTabPage(activeTabId, currentPage + 1);
                }}
                onDocumentLoad={({ doc }) => {
                    const documentUrl = selectedSource.document_url;
                    if (!activeTabId || !documentUrl || !holdTabPdf) return;
                    holdTabPdf(activeTabId, documentUrl, () => doc.getData());
                }}
                plugins={[
                    defaultLayoutPluginInstance,
                    highlightPluginInstance,
                    pageNavigationPluginInstance,
                    reportLinkPluginInstance,
                ]}
            />
        </Box>
    );
};
